import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Input, TextArea } from "@progress/kendo-react-inputs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Label } from "@progress/kendo-react-labels";
import { filterBy } from "@progress/kendo-data-query";
import { useNominalsBySubType } from "services/nominalsService";
import { useActiveVats } from "services/vatService";
import SearchComboBox from "components/common/SearchComboBox";
import CONSTANT from "CONSTANT/Matter/ComboxType";

const roundTo2 = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return parseFloat(numeric.toFixed(2));
};

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const vatKeyOf = (v) => {
  if (v && typeof v === "object") return v.vatCodeId ?? v.vatCode ?? "";
  return v ?? "";
};

const getFeeEarnerRef = (item) =>
  item?.userRef ?? item?.userID ?? item?.id ?? item?.value ?? "";

const toFeeEarnerOption = (item) => {
  if (!item) return null;
  const ref = getFeeEarnerRef(item);
  const name = item?.displayname ?? item?.displayName ?? item?.name ?? "";
  return {
    ...item,
    userRef: ref,
    display:
      item?.display ??
      item?.representation ??
      (ref && name ? `${ref} - ${name}` : String(ref || name || "")),
  };
};

const BillItemEditDialog = ({
  item,
  feeEarners = [],
  defaultVatCode = null,
  onClose,
  onSave,
}) => {
  const { data: activevatsdata = [] } = useActiveVats();
  const { data: incomeNominals = [] } = useNominalsBySubType(
    "ProfitAndLoss_Income",
    { excludeArchived: true },
  );

  const isProfitCost = item?.recordType === "Time" && item?.type === "ProfitCost";
  const isTime = item?.recordType === "Time" && !isProfitCost;
  const isExpense = item?.recordType === "Expense";
  const isDisbursement = item?.recordType === "Disbursement";

  const canEditCoreFields = isTime || isProfitCost || isExpense;
  const canEditDetails = isProfitCost || isExpense;
  const disbVatAmount = Number(item?.vatAmount ?? item?.displayVat ?? 0);
  const canEditDisbVatCode = isDisbursement && disbVatAmount === 0;
  const canEditVatCode = canEditCoreFields || canEditDisbVatCode;
  const isWriteOff = !isExpense && item?.billedNetAmount === "w/o";

  const vatOptions = useMemo(() => activevatsdata || [], [activevatsdata]);
  const nominalOptions = useMemo(() => incomeNominals || [], [incomeNominals]);

  const fallbackVatKey = useMemo(() => {
    const itemKey = vatKeyOf(item?.vatCode);
    if (itemKey !== "" && itemKey !== null && itemKey !== undefined) {
      return itemKey;
    }
    if (isTime || isProfitCost) return vatKeyOf(defaultVatCode);
    return "";
  }, [item?.vatCode, defaultVatCode, isTime, isProfitCost]);

  const [selectedVat, setSelectedVat] = useState(null);
  const [selectedFeeEarner, setSelectedFeeEarner] = useState(null);
  const [selectedNominal, setSelectedNominal] = useState(null);
  const [nominalFilter, setNominalFilter] = useState(null);
  const [netAmount, setNetAmount] = useState("");
  const [details, setDetails] = useState("");

  useEffect(() => {
    setSelectedVat(null);
    setSelectedFeeEarner(null);
    setSelectedNominal(null);
    setNominalFilter(null);
    setNetAmount(
      isExpense
        ? String(item?.netAmount ?? 0)
        : isWriteOff
          ? ""
          : String(item?.billedNetAmount ?? item?.displayNet ?? 0),
    );
    setDetails(item?.details ?? "");
  }, [item, isExpense, isWriteOff]);

  useEffect(() => {
    if (selectedVat || !fallbackVatKey || vatOptions.length === 0) return;
    const matchedVat =
      vatOptions.find(
        (v) =>
          String(v.vatCodeId) === String(fallbackVatKey) ||
          String(v.vatCode) === String(fallbackVatKey),
      ) || null;
    if (matchedVat) {
      setSelectedVat(matchedVat);
    }
  }, [fallbackVatKey, vatOptions, selectedVat]);

  useEffect(() => {
    if (selectedNominal || !item?.nominalRef || nominalOptions.length === 0)
      return;
    const matchedNominal =
      nominalOptions.find(
        (n) => String(n.nominalRef) === String(item.nominalRef),
      ) || null;
    if (matchedNominal) {
      setSelectedNominal(matchedNominal);
    }
  }, [item?.nominalRef, nominalOptions, selectedNominal]);

  useEffect(() => {
    if (selectedFeeEarner) return;
    const existingFeeEarner = item?.billFeeEarnerRef || item?.feeEarnerRef;
    if (!existingFeeEarner) return;
    const matchedFeeEarner = feeEarners.find(
      (f) => String(getFeeEarnerRef(f)) === String(existingFeeEarner),
    );
    if (matchedFeeEarner) {
      setSelectedFeeEarner(toFeeEarnerOption(matchedFeeEarner));
    } else if (feeEarners.length > 0) {
      setSelectedFeeEarner({
        userRef: existingFeeEarner,
        display: String(existingFeeEarner),
      });
    }
  }, [item?.billFeeEarnerRef, item?.feeEarnerRef, feeEarners, selectedFeeEarner]);

  const nominalData = useMemo(() => {
    if (!nominalFilter) return nominalOptions;
    return filterBy(nominalOptions.slice(), nominalFilter);
  }, [nominalOptions, nominalFilter]);

  const onFilterNominal = (event) => {
    setNominalFilter(event.filter);
  };

  const dialogTitle = isProfitCost
    ? "Edit Profit Cost"
    : isTime
      ? "Edit Time"
      : isExpense
        ? "Edit Expense"
        : "Edit Disbursement";

  const isSaveDisabled =
    (canEditCoreFields &&
      (!selectedVat ||
        !selectedNominal ||
        !getFeeEarnerRef(selectedFeeEarner))) ||
    (isDisbursement && (!canEditDisbVatCode || !selectedVat));

  const handleSave = () => {
    const nextItem = { ...item };
    const effectiveVat =
      selectedVat ||
      (fallbackVatKey
        ? vatOptions.find(
            (v) =>
              String(v.vatCodeId) === String(fallbackVatKey) ||
              String(v.vatCode) === String(fallbackVatKey),
          )
        : undefined);

    if (canEditVatCode && effectiveVat) {
      nextItem.vatCode = effectiveVat.vatCodeId;
    }

    if (canEditCoreFields) {
      const nextNet = roundTo2(toNumber(netAmount));
      const nextVat = roundTo2(nextNet * ((effectiveVat?.rate || 0) / 100));
      nextItem.nominalRef = selectedNominal?.nominalRef ?? item?.nominalRef ?? "";
      const feeRef = getFeeEarnerRef(selectedFeeEarner) || item?.billFeeEarnerRef || item?.feeEarnerRef || "";

      if (isExpense) {
        nextItem.netAmount = nextNet;
        nextItem.vatAmount = nextVat;
        nextItem.feeEarnerRef = feeRef;
      } else {
        const netTouched = String(netAmount).trim() !== "";
        if (isWriteOff && !netTouched) {
          nextItem.billedNetAmount = "w/o";
        } else {
          nextItem.billedNetAmount = nextNet;
          nextItem.billVat = nextVat;
        }
        nextItem.billFeeEarnerRef = feeRef;
      }
    }

    if (canEditDisbVatCode && effectiveVat) {
      const disbNet = roundTo2(toNumber(item?.netAmount));
      nextItem.vatAmount = roundTo2(disbNet * ((effectiveVat?.rate || 0) / 100));
      nextItem.vatRate = effectiveVat?.rate ?? 0;
    }

    if (canEditDetails) {
      nextItem.details = details ?? "";
    }

    onSave(nextItem);
  };

  return (
    <Dialog title={dialogTitle} onClose={onClose} width={460} className="dialog-sm">
      <div className="dialog-content p-3 py-0">
        <div className="bgForm p-3">
        {canEditVatCode && (
          <div className="mb-3 d-flex flex-column">
            <SearchComboBox
              name="vatCode"
              cbType={CONSTANT.AVATS}
              customCombo={true}
              width="auto"
              boxShadow={false}
              value={selectedVat}
              onChangeFunction={(e) => setSelectedVat(e?.value || null)}
              placeholder="Search VAT Code"
              className="mt-1"
              style={{ width: "100%" }}
            />
          </div>
        )}

        {canEditCoreFields && (
          <>
            <div className="mb-3 d-flex flex-column">
              <Label className="label">Nominal Ref</Label>
              <ComboBox
              className="mt-1"
                data={nominalData}
              textField="display"
                value={selectedNominal}
                onChange={(e) => setSelectedNominal(e.value)}
                filterable={true}
                onFilterChange={onFilterNominal}
                placeholder="Search Nominal Ref"
                style={{
                  width: "100%",
                  border: !selectedNominal ? "1px solid #d1d1d1" : undefined,
                }}
              />
            </div>

            <div className="mb-3 d-flex flex-column">
              <SearchComboBox
                name="feeEarner"
                cbType={CONSTANT.FE}
                customCombo={true}
                value={selectedFeeEarner}
                onChangeFunction={(e) =>
                  setSelectedFeeEarner(toFeeEarnerOption(e?.value) || null)
                }
                width="auto"
                boxShadow={false}
                className="mt-1"
                placeholder="Search Fee Earner Ref"
                style={{ width: "100%" }}
              />
            </div>

            <div className="mb-3 d-flex flex-column">
              <Label className="label">Bill Net</Label>
              <Input
                type="number"
                step="0.01"
                value={netAmount}
                onChange={(e) => setNetAmount(e.value)}
                placeholder={
                  isWriteOff ? "Write-off — enter amount to convert" : undefined
                }
                className="mt-1"
              style={{ width: "100%" }}
              />
            </div>
          </>
        )}

        {canEditDetails && (
          <div className="mb-3 d-flex flex-column">
            <Label>Details</Label>
            <TextArea
              rows={3}
              value={details}
              onChange={(e) => setDetails(e.value)}
              className="mt-1"
              style={{ width: "100%" }}
            />
          </div>
        )}

        {isDisbursement && !canEditDisbVatCode && (
          <p className="mb-2 text-muted" style={{ fontSize: "12px" }}>
            VAT Code can only be edited when current VAT Amount is zero.
          </p>
        )}
        </div>
      </div>

      <DialogActionsBar>
        <div className="dialog-footer w-100">
          <Button className="common-btn bgBtnSecondary me-2" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            className="common-btn me-3"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSave();
            }}
            disabled={isSaveDisabled}>
            Save
          </Button>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
};

export default BillItemEditDialog;
